import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Button,
  Spinner,
  Text,
  mergeClasses,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  ReOrderDotsVertical20Regular,
  DeleteRegular,
  AddRegular,
  DismissCircleRegular,
  CircleRegular,
} from '@fluentui/react-icons';

export const CATALOG_COLORS = [
  '#3b82f6', '#f59e0b', '#10b981', '#ef4444',
  '#8b5cf6', '#f97316', '#06b6d4', '#ec4899',
];
import type { Catalog } from '@supernova/shared';
import { getPaisesVisibles } from '../api/catalogs.js';
import { catalogUsable, catalogGenerating, catalogStatusLabel } from '../lib/catalogStatus.js';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase300 },
  hint: { fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground3 },
  list: { display: 'flex', flexDirection: 'column', gap: '4px' },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '4px',
    padding: '8px 9px',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-lg)',
    background: 'var(--bg-surface)',
    transition: 'border-color 0.1s, background 0.1s',
  },
  itemOficial: {
    background: 'var(--oficial-weak)',
    border: '1px solid var(--oficial)',
  },
  itemDragging: { opacity: 0.35 },
  itemDragOver: { ...shorthands.borderColor(tokens.colorBrandBackground) },
  itemExcluded: {
    opacity: 0.55,
    background: tokens.colorNeutralBackground2,
  },
  dragHandle: {
    cursor: 'grab',
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    paddingTop: '1px',
    display: 'flex',
    alignItems: 'center',
    ':active': { cursor: 'grabbing' },
  },
  dragHandlePlaceholder: { width: '20px', flexShrink: 0 },
  itemName: {
    flex: 1,
    fontSize: tokens.fontSizeBase100,
    minWidth: 0,
    lineHeight: '1.35',
    wordBreak: 'break-word',
    overflow: 'hidden',
    paddingTop: '2px',
  },
  generatingNote: {
    display: 'block',
    fontSize: '10px',
    color: tokens.colorBrandForeground1,
    fontStyle: 'italic',
    marginTop: '1px',
  },
  priority: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontFamily: 'var(--font-mono, monospace)',
    minWidth: '18px',
    textAlign: 'right',
    flexShrink: 0,
    paddingTop: '3px',
  },
  // Separador ROJO entre catálogos EN USO (arriba) y no usados (abajo). Doble
  // línea ("par de líneas") para que sea inconfundible incluso colapsado.
  divider: {
    borderTop: '2px solid #e5484d',
    borderBottom: '2px solid #e5484d',
    height: '3px',
    margin: '7px 2px',
    flexShrink: 0,
  },
  statusIcon: { flexShrink: 0, fontSize: '18px', paddingTop: '0px' },
  empty: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200, fontStyle: 'italic' },
  groupTitle: {
    marginTop: '10px', marginBottom: '2px', fontSize: tokens.fontSizeBase100, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.04em', textTransform: 'uppercase', color: tokens.colorNeutralForeground3,
  },
  atribucion: { display: 'block', fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground3, lineHeight: '1.3' },
  actionBtn: {
    minWidth: 0,
    padding: '2px 4px',
    flexShrink: 0,
  },
  actionPlaceholder: { width: '24px', flexShrink: 0 },
});

interface Props {
  available: Catalog[];
  initialSelected: string[];
  onChange: (catalogIds: string[]) => void;
  disabled?: boolean;
}

export function ProjectCatalogsPicker({ available, initialSelected, onChange, disabled }: Props) {
  const styles = useStyles();
  const [selected, setSelected] = useState<string[]>(initialSelected);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const dragNode = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setSelected(initialSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelected.join(',')]);

  const availableMap = useMemo(
    () => Object.fromEntries(available.map((c) => [c.id, c])),
    [available],
  );

  // Colores estables por catálogo (basados en posición en `available`, no en `selected`)
  const catalogColorMap = useMemo(
    () => Object.fromEntries(
      available.map((c, i) => [c.id, CATALOG_COLORS[i % CATALOG_COLORS.length]!])
    ),
    [available],
  );

  const notify = useCallback(
    (next: string[]) => {
      setSelected(next);
      onChange(next);
    },
    [onChange],
  );

  function exclude(catalogId: string) {
    notify(selected.filter((id) => id !== catalogId));
  }

  function include(catalogId: string) {
    if (selected.includes(catalogId)) return;
    notify([...selected, catalogId]);
  }

  // ── Drag & drop handlers ──────────────────────────────────────────────────

  function onDragStart(e: React.DragEvent<HTMLDivElement>, idx: number) {
    if (disabled) { e.preventDefault(); return; }
    dragNode.current = e.currentTarget;
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    // Necesario en Firefox para iniciar el drag
    e.dataTransfer.setData('text/plain', String(idx));
  }

  function onDragOver(e: React.DragEvent, idx: number) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  }

  function onDrop(e: React.DragEvent, idx: number) {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const next = [...selected];
    const [item] = next.splice(dragIdx, 1);
    next.splice(idx, 0, item!);
    notify(next);
    setDragIdx(null);
    setDragOverIdx(null);
  }

  function onDragEnd() {
    setDragIdx(null);
    setDragOverIdx(null);
    dragNode.current = null;
  }

  // ─────────────────────────────────────────────────────────────────────────

  // Países que el cliente eligió en el portal (mundito): los eCostos y los públicos de otros
  // países no se ofrecen aquí (los que ya estén en la cotización siguen visibles arriba).
  const [paisesVisibles, setPaisesVisibles] = useState<string[] | null>(null);
  useEffect(() => {
    let vivo = true;
    void getPaisesVisibles().then((p) => { if (vivo) setPaisesVisibles(p); });
    return () => { vivo = false; };
  }, []);

  const excluded = useMemo(
    () => available.filter((c) => {
      if (selected.includes(c.id)) return false;
      const dePais = c.origen === 'ecosoft' || c.origen === 'publico' || c.kind === 'ecostos';
      if (!dePais || !paisesVisibles) return true;
      return paisesVisibles.includes((c.country || '').toUpperCase());
    }),
    [available, selected, paisesVisibles],
  );

  // Agrupación de los disponibles: los eCostos de Ecosoft, los catálogos PÚBLICOS de cada país
  // (bases oficiales/abiertas que eko27 publica) y el resto (del cliente u otros).
  const grupos = useMemo(() => {
    const eco = excluded.filter((c) => c.origen === 'ecosoft' || c.kind === 'ecostos');
    const pub = excluded.filter((c) => c.origen === 'publico');
    const otros = excluded.filter((c) => !eco.includes(c) && !pub.includes(c));
    const porPais = new Map<string, Catalog[]>();
    for (const c of pub) {
      const k = (c.country || '—').toUpperCase();
      porPais.set(k, [...(porPais.get(k) ?? []), c]);
    }
    return [
      { titulo: 'Catálogos Ecosoft', items: eco },
      ...[...porPais.entries()]
        .sort(([a], [b]) => nombrePais(a).localeCompare(nombrePais(b), 'es'))
        .map(([pais, items]) => ({ titulo: `Catálogos públicos · ${nombrePais(pais)}`, items })),
      { titulo: 'Otros catálogos', items: otros },
    ].filter((g) => g.items.length > 0);
  }, [excluded]);

  if (available.length === 0) {
    return (
      <div className={styles.root}>
        <Text className={styles.label}>Catálogos de referencia</Text>
        <Text className={styles.empty}>No hay catálogos de referencia disponibles.</Text>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <Text className={styles.label}>Catálogos de referencia</Text>
      <Text className={styles.hint}>
        Arrastra para ordenar. El primero es el que más pesa. Puedes quitar eCostos si no lo quieres usar.
      </Text>

      <div className={styles.list}>

        {/* ── Catálogos seleccionados ── */}
        {selected.length === 0 && (
          <Text className={styles.empty}>Ninguno seleccionado. Usa + para agregar.</Text>
        )}

        {selected.map((id, idx) => {
          const cat = availableMap[id];
          if (!cat) return null;
          const color = catalogColorMap[id] ?? CATALOG_COLORS[0]!;
          const isDragging = dragIdx === idx;
          const isDragOver = dragOverIdx === idx && dragIdx !== idx;
          return (
            <div
              key={id}
              draggable={!disabled}
              className={mergeClasses(
                styles.item,
                (cat.kind === 'ecostos' || cat.kind === 'referencia') && styles.itemOficial,
                isDragging && styles.itemDragging,
                isDragOver && styles.itemDragOver,
              )}
              onDragStart={(e) => onDragStart(e, idx)}
              onDragOver={(e) => onDragOver(e, idx)}
              onDrop={(e) => onDrop(e, idx)}
              onDragEnd={onDragEnd}
            >
              <span
                className={styles.dragHandle}
                style={{ visibility: disabled ? 'hidden' : 'visible' }}
                title="Arrastra para reordenar"
              >
                <ReOrderDotsVertical20Regular />
              </span>
              <span
                style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: color, flexShrink: 0, marginTop: '4px',
                }}
              />
              <Text className={styles.itemName} title={cat.displayName?.trim() && cat.displayName !== cat.name ? `${cat.displayName} (${cat.name})` : cat.name}>
                {cat.displayName?.trim() || cat.name}
                {catalogGenerating(cat) && (
                  <span className={styles.generatingNote}>{catalogStatusLabel(cat)}</span>
                )}
              </Text>
              <Text className={styles.priority}>#{idx + 1}</Text>
              <Button
                size="small"
                appearance="subtle"
                icon={<DeleteRegular />}
                className={styles.actionBtn}
                disabled={disabled}
                onClick={() => exclude(id)}
                title="Excluir de la cotización"
              />
            </div>
          );
        })}

        {/* ── Separador ── */}
        {excluded.length > 0 && <div className={styles.divider} />}

        {/* ── Catálogos disponibles, agrupados: Ecosoft · públicos por país · otros ── */}
        {grupos.map((g) => (
          <div key={g.titulo} role="group" aria-label={g.titulo}>
            <Text as="p" className={styles.groupTitle}>{g.titulo}</Text>
        {g.items.map((cat) => {
          const canAdd = catalogUsable(cat);
          const generating = catalogGenerating(cat);
          return (
            <div key={cat.id} className={mergeClasses(styles.item, styles.itemExcluded)}>
              <div className={styles.dragHandlePlaceholder} />
              {generating ? (
                // Generándose/actualizándose: spinner (NO ícono de error) para que
                // se vea que el catálogo SÍ está y está procesándose, no que falló.
                <span className={styles.statusIcon}><Spinner size="extra-tiny" /></span>
              ) : cat.status === 'failed' ? (
                // Solo un catálogo que falló lleva la X roja. Uno listo que todavía no se
                // agregó a esta cotización no es un error: va un círculo neutro y el «+».
                <DismissCircleRegular
                  className={styles.statusIcon}
                  style={{ color: 'var(--colorPaletteRedForeground1)' }}
                />
              ) : (
                <CircleRegular className={styles.statusIcon} style={{ color: 'var(--colorNeutralForeground3)' }} />
              )}
              <Text className={styles.itemName} title={cat.displayName?.trim() && cat.displayName !== cat.name ? `${cat.displayName} (${cat.name})` : cat.name}>
                {cat.displayName?.trim() || cat.name}
                {generating && (
                  <span className={styles.generatingNote}>{catalogStatusLabel(cat)}</span>
                )}
                {cat.origen === 'publico' && cat.atribucion && (
                  <span className={styles.atribucion}>{cat.atribucion}</span>
                )}
              </Text>
              <div className={styles.actionPlaceholder} />
              <Button
                size="small"
                appearance="subtle"
                icon={<AddRegular />}
                className={styles.actionBtn}
                disabled={disabled || !canAdd}
                onClick={() => include(cat.id)}
                title={
                  canAdd
                    ? 'Agregar a la cotización'
                    : generating
                      ? `${catalogStatusLabel(cat)} — disponible al terminar`
                      : `No disponible (${cat.status})`
                }
              />
            </div>
          );
        })}
          </div>
        ))}

      </div>
    </div>
  );
}

/** Nombre del país para los encabezados del selector (ISO alpha-2 → nombre). */
const NOMBRES_PAIS: Record<string, string> = {
  MX: 'México', AR: 'Argentina', BO: 'Bolivia', BR: 'Brasil', CA: 'Canadá', CL: 'Chile', CO: 'Colombia',
  DO: 'República Dominicana', EC: 'Ecuador', ES: 'España', EU: 'Unión Europea', PE: 'Perú', PY: 'Paraguay',
  US: 'Estados Unidos', UY: 'Uruguay',
};
function nombrePais(code: string): string {
  return NOMBRES_PAIS[code] ?? code;
}
